import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { type SystemChipItem, SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";

type Props = {
	/** The windows' length in hours, as the grouping keyed it. */
	length: string;
	windows: [DateTime, DateTime][];
	selectedStart: DateTime;
	selectedEnd: DateTime;
	onPick: (start: DateTime, end: DateTime) => void;
};

/** Two instants are the same time — compared by value, never by object identity. */
const isSameInstant = (a: DateTime, b: DateTime): boolean => a.toMillis() === b.toMillis();

/** One length's full-shift windows in `EditTimeTouchWindows`: the "12 hours" label over its row of touch chips. */
export const EditTimeTouchWindowGroup: FC<Props> = ({ length, windows, selectedStart, selectedEnd, onPick }) => {
	const { t } = useTranslation();
	const items = useMemo<SystemChipItem<string>[]>(
		() => windows.map(([start, end], index) => ({ value: `${length}-${index}`, label: `${start.toFormat("h:mm a")} – ${end.toFormat("h:mm a")}` })),
		[windows, length],
	);
	const selected = items.filter((_, index) => isSameInstant(windows[index][0], selectedStart) && isSameInstant(windows[index][1], selectedEnd));
	return (
		<Box sx={{ mt: 1.25 }}>
			<Typography
				component="h5"
				data-testid={`edit-time-length-${length}`}
				sx={{ m: 0, mb: 0.5, fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: INK.SECONDARY }}
			>
				{t("edit.time.touch.lengthGroup", { hours: length })}
			</Typography>
			<SystemChips<string>
				items={items}
				selectedItems={selected}
				onChange={(item) => {
					const index = items.findIndex((candidate) => candidate.value === item.value);
					onPick(windows[index][0], windows[index][1]);
				}}
				variant="filled"
				touchTarget
				// Start-aligned like the labels above them; without it a short row is centred in the sheet.
				forceFitContent
				getItemDataTestId={(item) => `edit-time-shift-option-${item.value}`}
				rootSx={{ justifyContent: "flex-start" }}
			/>
		</Box>
	);
};
